/* eslint-disable camelcase */
import { Component, computed, OnInit, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { HumiInquiryStatus } from '@app/models/kyc-kyb/types';
import { DocumentStatus } from '@app/models/payroll/self-onboarding-document.model';
import { originalKeyValueOrder } from '@app/modules/import/functions/original-key-value-order';
import { SelfOnboardingDocumentsService } from '@app/modules/payroll/services';
import {
    AuthorizationDocumentType,
    PAYROLL_AUTHORIZATION_DOCUMENTS,
} from '@app/modules/payroll/types/self-onboarding-document-type.type';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { KYCKYBService } from '@app/services/kyc-kyb/kyc-kyb.service';
import { TranslatableKey } from '@app/types/translatable.type';
import { BadgeType } from '@humi-design-library/components/Badge/types';
import { take } from 'rxjs/operators';

@Component({
    templateUrl: './authorization-documents.template.html',
    styleUrls: ['../../../self-serve.style.scss'],
})
export class AuthorizationDocumentsView implements OnInit {
    isLoading = true;
    isAllUploaded = false;
    isKYBEnabled = signal(false);
    KYBStatus = signal<HumiInquiryStatus | undefined>(undefined);
    isKYBIncomplete: Signal<boolean> = computed(() => {
        const status = this.KYBStatus();
        if (status !== undefined) {
            return this.knowYourService.isIncompleteState(status);
        }
        return false;
    });
    originalKeyValueOrder = originalKeyValueOrder;

    /**
     * the document-related steps that will be shown on the page. We default to not signed
     * and add details as data is loaded in.
     */
    documentSteps: Record<
        AuthorizationDocumentType,
        { title: string; description: string; status: DocumentStatus; rejectionMessage: string }
    > = {
        PadAgreement: {
            title: 'self-serve.payrollDocuments.preauthorizedDebitAgreement',
            description: 'self-serve.payrollDocuments.preauthorizedDebitAgreementDescription',
            status: 'new',
            rejectionMessage: '',
        },
        ROEAuthorization: {
            title: 'self-serve.payrollDocuments.submitRoes',
            description: 'self-serve.payrollDocuments.submitRoesDescription',
            status: 'new',
            rejectionMessage: '',
        },
    };

    /**
     * The badge type and label for each document status
     */
    BADGE_STATUS_MAPPING: Omit<Record<DocumentStatus, { badgeType: BadgeType; label: TranslatableKey }>, 'new'> & {
        new: undefined;
    } = {
        ready_for_review: { badgeType: 'warning', label: 'self-serve.payrollDocuments.inReview' },
        verified: { badgeType: 'success', label: 'self-serve.payrollDocuments.approved' },
        rejected: { badgeType: 'danger', label: 'self-serve.payrollDocuments.rejected' },
        new: undefined,
    } as const;

    constructor(
        private analyticService: AnalyticService,
        private authService: AuthService,
        private notifyService: NotifyService,
        private router: Router,
        private selfOnboardingDocumentsService: SelfOnboardingDocumentsService,
        private featureService: FeatureService,
        private knowYourService: KYCKYBService
    ) {
        this.featureService.has(FeatureFlag.KYC_KYB).then(this.isKYBEnabled.set);
        if (this.isPayrollSyncEnabled) {
            this.knowYourService.companyStatus.pipe(take(1)).subscribe(this.KYBStatus.set);
        }
    }

    get isPayrollSyncEnabled(): boolean {
        return this.authService.company.isPayrollSyncEnabled;
    }

    async ngOnInit(): Promise<void> {
        try {
            this.analyticService.trackEvent(AnalyticEvents.SelfServePayrollDocumentsClickAuthorizationTab);

            if (!this.isPayrollSyncEnabled) {
                this.isLoading = false;
                return;
            }

            // load documents from payroll
            const documents = await this.selfOnboardingDocumentsService.getDocuments(PAYROLL_AUTHORIZATION_DOCUMENTS);

            let hasIncompleteDocuments = false;

            // Updates status and rejectionMessage (if present)
            PAYROLL_AUTHORIZATION_DOCUMENTS.forEach((documentStep) => {
                this.documentSteps[documentStep] = {
                    ...this.documentSteps[documentStep],
                    status: documents[documentStep].state,
                    rejectionMessage: documents[documentStep].rejectionReason,
                };
                if (documents[documentStep].state === 'new') {
                    hasIncompleteDocuments = true;
                }
            });

            // Used to change text on button that redirects to authorization-documents
            this.isAllUploaded = !hasIncompleteDocuments;
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    navigateToSelfServePayrollSignDocuments(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServePayrollDocumentsClickViewAuthorizationDocuments);

        this.router.navigate(['/payroll', 'self-onboarding', 'authorization-documents']);
    }

    navigateToPayrollBusinessVerification(): void {
        this.router.navigate(['/company', 'verify-your-business']);
    }
}
